import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Stat, Panel, StatusDot, PageHead } from "@/components/kit";
import QuotaPanel from "@/components/QuotaPanel";
import AlertsBanner from "@/components/AlertsBanner";
import { Cpu, Radio, Upload, Languages, Moon, ShieldCheck, ShieldAlert } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const LANG_NAME = { en: "EN", pt: "PT", de: "DE", fr: "FR" };
const fmtN = (n) => Number(n || 0).toLocaleString("es");

const Prog = ({ label, value, goal, fmt = fmtN, testId }) => {
    const pct = Math.min(100, Math.round(((value || 0) / (goal || 1)) * 100));
    const color = pct >= 100 ? "#39FF14" : pct >= 60 ? "#FFD700" : "#39A0FF";
    return (
        <div className="mb-3" data-testid={testId}>
            <div className="mb-1 flex justify-between text-[11px]">
                <span className="uppercase tracking-wider text-muted-foreground">{label}</span>
                <span style={{ color }}>{fmt(value)} / {fmt(goal)}</span>
            </div>
            <div className="h-1.5 w-full bg-secondary">
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
};

export default function Dashboard() {
    const { data } = useQuery({
        queryKey: ["stats"],
        queryFn: async () => (await api.get("/dashboard/stats")).data,
        refetchInterval: 4000,
    });

    const s = data || {};
    const yt = s.yt_status || {};
    const engine = s.engine || {};
    const uploadPct = Math.min(100, Math.round(((yt.uploads_24h || 0) / (yt.upload_target || 1)) * 100));
    const uploadColor = uploadPct >= 100 ? "#FF003C" : uploadPct >= 80 ? "#FFD700" : "#39FF14";

    return (
        <div data-testid="dashboard-page">
            <PageHead
                title="COMMAND CENTER"
                sub="Orquestador autónomo 24/7 · Investigación → Kit de canal → Producción Pixelle-Video → Publicación yutu → Análisis con feedback loop."
            />

            <AlertsBanner />

            <div className="grid grid-cols-2 gap-px bg-border lg:grid-cols-6">
                <Stat testId="stat-channels" label="Canales" value={s.channels ?? "—"} />
                <Stat testId="stat-niches" label="Nichos" value={s.niches ?? "—"} />
                <Stat testId="stat-producing" label="En producción" value={s.producing ?? "—"} accent />
                <Stat testId="stat-pending" label="Por aprobar" value={s.pending_approvals ?? "—"} accent />
                <Stat testId="stat-published" label="Publicados" value={s.published ?? "—"} />
                <Stat testId="stat-scheduled" label="Agendados" value={s.scheduled ?? "—"} />
            </div>

            <div className="mt-6">
                <QuotaPanel />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <Panel title="Estado · YouTube (real)" testId="panel-yt-status" className="lg:col-span-1">
                    {!yt.linked ? (
                        <p className="text-xs text-muted-foreground">Sin canal de YouTube vinculado.</p>
                    ) : (
                        <div className="space-y-4">
                            {/* Marcapasos de subidas (rolling 24h) — el límite #1 */}
                            <div data-testid="yt-upload-pacer">
                                <div className="mb-1 flex items-center justify-between text-[11px]">
                                    <span className="flex items-center gap-1.5 uppercase tracking-wider text-muted-foreground">
                                        <Upload size={12} /> Subidas (24h)
                                    </span>
                                    <span style={{ color: uploadColor }}>
                                        {yt.uploads_24h ?? 0} / {yt.upload_target ?? "—"}
                                    </span>
                                </div>
                                <div className="h-1.5 w-full bg-secondary">
                                    <div className="h-full transition-all" style={{ width: `${uploadPct}%`, background: uploadColor }} />
                                </div>
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    Límite real aprendido de YouTube.
                                    {yt.in_flight ? ` ${yt.in_flight} subiéndose.` : ""}
                                    {yt.queued_blocked ? ` ${yt.queued_blocked} en cola por cupo.` : ""}
                                </p>
                            </div>

                            {/* Cadencia + idiomas */}
                            <div className="flex items-center justify-between text-xs" data-testid="yt-cadence">
                                <span className="flex items-center gap-1.5 uppercase tracking-wider text-muted-foreground">
                                    <Languages size={12} /> Cadencia
                                </span>
                                <span className="text-right text-foreground">
                                    {yt.videos_per_day}/día × (ES{yt.dub_enabled && yt.dub_languages?.length
                                        ? "+" + yt.dub_languages.map((l) => LANG_NAME[l] || l).join("+") : ""})
                                    <span className="block text-[10px] text-muted-foreground">
                                        = {yt.videos_per_day * (1 + (yt.dub_enabled ? (yt.dub_languages?.length || 0) : 0))} subidas/día
                                    </span>
                                </span>
                            </div>

                            {/* Día de descanso + verificación */}
                            <div className="flex items-center justify-between text-xs" data-testid="yt-rest">
                                <span className="flex items-center gap-1.5 uppercase tracking-wider text-muted-foreground">
                                    <Moon size={12} /> Descanso
                                </span>
                                <span className={yt.today_is_rest ? "text-[#FFD700]" : "text-foreground"}>
                                    {yt.rest_days?.length ? yt.rest_days.map((d) => DAYS[d]).join(", ") : "Ninguno"}
                                    {yt.today_is_rest ? " · hoy no se produce" : ""}
                                    <span className="block text-[10px] text-muted-foreground">
                                        Sin producción nueva; la cola SÍ se sube ese día
                                    </span>
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-xs" data-testid="yt-verified">
                                <span className="flex items-center gap-1.5 uppercase tracking-wider text-muted-foreground">
                                    {yt.verified ? <ShieldCheck size={12} className="text-[#39FF14]" /> : <ShieldAlert size={12} className="text-[#FFD700]" />}
                                    Miniaturas IA
                                </span>
                                <span style={{ color: yt.verified ? "#39FF14" : "#FFD700" }}>
                                    {yt.verified ? "Activas ✓" : "Aún no aplicadas"}
                                </span>
                            </div>

                            {/* Progreso a monetización (YPP) con datos reales */}
                            <div className="border-t border-border pt-3">
                                <div className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Camino a monetización (YPP)
                                </div>
                                <Prog testId="yt-subs" label="Suscriptores" value={yt.subs} goal={yt.subs_goal} />
                                <Prog testId="yt-watch" label="Horas long (365d)" value={yt.watch_hours} goal={yt.watch_goal}
                                      fmt={(n) => `${fmtN(Math.round(n))}h`} />
                                <Prog testId="yt-shorts" label="Vistas Shorts (90d)" value={yt.shorts_views} goal={yt.shorts_goal} />
                            </div>
                        </div>
                    )}
                </Panel>

                <Panel title="Motores del ecosistema" testId="panel-engines" className="lg:col-span-1">
                    <ul className="space-y-3 text-sm">
                        {Object.entries(engine).map(([k, v]) => (
                            <li key={k} className="flex items-center justify-between">
                                <span className="flex items-center gap-2 uppercase tracking-wider text-xs">
                                    <Cpu size={13} className="text-muted-foreground" /> {k}
                                </span>
                                <span className="flex items-center gap-2 text-xs">
                                    <StatusDot
                                        color={["online", "real", "render-integrado"].includes(v) ? "#39FF14" : "#FFD700"}
                                        pulse={["online", "real", "render-integrado"].includes(v)}
                                    />
                                    {v}
                                </span>
                            </li>
                        ))}
                    </ul>
                </Panel>

                <Panel title="Registro de auditoría" testId="panel-activity" className="lg:col-span-1">
                    <ul className="space-y-3">
                        {(s.recent_activity || []).map((a) => (
                            <li key={a.id} className="flex gap-3 text-xs">
                                <Radio size={12} className="mt-0.5 shrink-0 text-primary" />
                                <div>
                                    <span className="text-primary">{a.actor}</span>{" "}
                                    <span className="text-muted-foreground">{a.detail}</span>
                                </div>
                            </li>
                        ))}
                        {!s.recent_activity?.length && (
                            <li className="text-xs text-muted-foreground">Sin actividad todavía.</li>
                        )}
                    </ul>
                </Panel>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {[
                    ["Investigar nichos", "/niches"],
                    ["Crear kit de canal", "/kit"],
                    ["Planear estrategia", "/strategy"],
                    ["Cola de producción", "/production"],
                    ["Aprobar videos", "/approvals"],
                ].map(([t, to]) => (
                    <Link
                        key={to}
                        to={to}
                        data-testid={`quick-${to.slice(1)}`}
                        className="border border-border bg-card p-4 text-xs uppercase tracking-wider transition-colors duration-150 hover:border-primary hover:text-primary"
                    >
                        {t} →
                    </Link>
                ))}
            </div>
        </div>
    );
}
